import axios, { AxiosError, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import type {
  User,
  JwtUser,
  AuthData,
  RefreshData,
  Post,
  Category,
  Tag,
  Comment,
  UpvoteState,
  AdPlacement,
  NewsletterSubscriber,
  AuditLog,
  AdminComment,
  AdminStats,
  PaginationMeta,
  PostListParams,
  AdminPostListParams,
  AdminUserListParams,
  AdminCommentListParams,
  AdminAuditParams,
  AdminNewsletterParams,
  CreatePostPayload,
  UpdatePostPayload,
  CreateCommentPayload,
  CreateAdPayload,
  UpdateAdPayload,
  UpdateUserPayload,
  SearchParams,
  AdZone,
} from '@shared/index';

// Axios instance
const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export const api = axios.create({
  baseURL:         `${BASE_URL}/api`,
  withCredentials: true,
  headers:         { 'Content-Type': 'application/json' },
});

// In-memory access token (never localStorage)
let _accessToken: string | null = null;

export const tokenStore = {
  get:   (): string | null      => _accessToken,
  set:   (token: string | null) => { _accessToken = token; },
  clear: ()                     => { _accessToken = null; },
};

// Request interceptor — attach Bearer from memory
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor — silent token refresh on 401
let _isRefreshing = false;
let _queue: Array<{ resolve: (t: string) => void; reject: (e: unknown) => void }> = [];

function flushQueue(error: unknown, token: string | null): void {
  _queue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token!)));
  _queue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const is401        = error.response?.status === 401;
    const isRefreshReq = original?.url?.includes('/auth/refresh');

    if (!is401 || original?._retry || isRefreshReq) return Promise.reject(error);

    if (_isRefreshing) {
      return new Promise<string>((resolve, reject) => _queue.push({ resolve, reject })).then(
        (token) => { original.headers.Authorization = `Bearer ${token}`; return api(original); },
      );
    }

    original._retry = true;
    _isRefreshing   = true;

    try {
      const { data } = await axios.post<{ success: true; data: RefreshData }>(
        `${BASE_URL}/api/auth/refresh`, {}, { withCredentials: true },
      );
      const newToken = data.data.accessToken;
      tokenStore.set(newToken);
      flushQueue(null, newToken);
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch (refreshErr) {
      flushQueue(refreshErr, null);
      tokenStore.clear();
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?redirect=${redirect}`;
      return Promise.reject(refreshErr);
    } finally {
      _isRefreshing = false;
    }
  },
);

// Helper — unwrap { success, data } envelope
async function unwrap<T>(promise: Promise<{ data: { success: true; data: T } }>): Promise<T> {
  const res = await promise;
  return res.data.data;
}

// Auth API
export const authApi = {
  login(email: string, password: string): Promise<AuthData> {
    return unwrap(api.post('/auth/login', { email, password }));
  },
  register(username: string, email: string, password: string, inviteCode?: string): Promise<AuthData> {
    return unwrap(api.post('/auth/register', { username, email, password, inviteCode }));
  },
  refresh(): Promise<RefreshData> {
    return unwrap(axios.post(`${BASE_URL}/api/auth/refresh`, {}, { withCredentials: true }));
  },
  logout(): Promise<void> {
    return api.post('/auth/logout').then(() => undefined);
  },
  me(): Promise<JwtUser> {
    return unwrap(api.get('/auth/me'));
  },
};

// Public API
export interface PublicPostsResult { data: Post[]; meta: PaginationMeta; }
export interface SearchResult { data: Post[]; meta: PaginationMeta & { query: string }; }

export const publicApi = {
  getPosts(params?: PostListParams): Promise<PublicPostsResult> {
    return api.get('/public/posts', { params }).then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },
  getFeaturedPosts(): Promise<Post[]> { return unwrap(api.get('/public/posts/featured')); },
  getPost(slug: string): Promise<Post> { return unwrap(api.get(`/public/posts/${slug}`)); },
  getCategories(): Promise<Category[]> { return unwrap(api.get('/public/categories')); },
  getTags(): Promise<Tag[]> { return unwrap(api.get('/public/tags')); },
  search(params: SearchParams): Promise<SearchResult> {
    return api.get('/public/search', { params }).then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },
  getAd(zone: AdZone): Promise<AdPlacement | null> { return unwrap(api.get(`/public/ads/${zone}`)); },
  getUpvotes(postId: string): Promise<UpvoteState> { return unwrap(api.get(`/public/upvotes/${postId}`)); },
  toggleUpvote(postId: string): Promise<UpvoteState> { return unwrap(api.post(`/public/upvotes/${postId}`)); },
  getComments(postId: string): Promise<Comment[]> { return unwrap(api.get(`/public/comments/${postId}`)); },
  createComment(postId: string, payload: CreateCommentPayload): Promise<Comment> {
    return unwrap(api.post(`/public/comments/${postId}`, payload));
  },
  subscribeNewsletter(email: string): Promise<{ email: string; confirmed: boolean }> {
    return unwrap(api.post('/public/newsletter', { email }));
  },
};

// Admin API
export interface AdminListResult<T> { data: T[]; meta: PaginationMeta; }

export const adminApi = {
  // Posts
  getPosts(params?: AdminPostListParams): Promise<AdminListResult<Post>> {
    return api.get('/admin/posts', { params }).then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },
  getPost(id: string): Promise<Post> { return unwrap(api.get(`/admin/posts/${id}`)); },
  createPost(payload: CreatePostPayload): Promise<Post> { return unwrap(api.post('/admin/posts', payload)); },
  updatePost(id: string, payload: UpdatePostPayload): Promise<Post> { return unwrap(api.put(`/admin/posts/${id}`, payload)); },
  deletePost(id: string): Promise<void> { return api.delete(`/admin/posts/${id}`).then(() => undefined); },
  publishPost(id: string): Promise<Post> { return unwrap(api.post(`/admin/posts/${id}/publish`)); },
  featurePost(id: string): Promise<Post> { return unwrap(api.post(`/admin/posts/${id}/feature`)); },
  // Comments
  getComments(params?: AdminCommentListParams): Promise<AdminListResult<AdminComment>> {
    return api.get('/admin/comments', { params }).then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },
  approveComment(id: string): Promise<Comment> { return unwrap(api.put(`/admin/comments/${id}/approve`)); },
  deleteComment(id: string): Promise<void> { return api.delete(`/admin/comments/${id}`).then(() => undefined); },
  // Users
  getUsers(params?: AdminUserListParams): Promise<AdminListResult<User>> {
    return api.get('/admin/users', { params }).then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },
  getUser(id: string): Promise<User> { return unwrap(api.get(`/admin/users/${id}`)); },
  updateUser(id: string, payload: UpdateUserPayload): Promise<User> { return unwrap(api.put(`/admin/users/${id}`, payload)); },
  revokeSessions(id: string): Promise<{ userId: string; redisKeysDeleted: number }> {
    return unwrap(api.post(`/admin/users/${id}/revoke-sessions`));
  },
  // Ads
  getAds(): Promise<AdPlacement[]> { return unwrap(api.get('/admin/ads')); },
  createAd(payload: CreateAdPayload): Promise<AdPlacement> { return unwrap(api.post('/admin/ads', payload)); },
  updateAd(id: string, payload: UpdateAdPayload): Promise<AdPlacement> { return unwrap(api.put(`/admin/ads/${id}`, payload)); },
  deleteAd(id: string): Promise<void> { return api.delete(`/admin/ads/${id}`).then(() => undefined); },
  toggleAd(id: string): Promise<AdPlacement> { return unwrap(api.post(`/admin/ads/${id}/toggle`)); },
  // Newsletter
  getNewsletterSubscribers(params?: AdminNewsletterParams): Promise<AdminListResult<NewsletterSubscriber>> {
    return api.get('/admin/newsletter', { params }).then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },
  deleteSubscriber(id: string): Promise<void> { return api.delete(`/admin/newsletter/${id}`).then(() => undefined); },
  exportSubscribers(): Promise<Blob> {
    return api.get('/admin/newsletter/export', { responseType: 'blob' }).then((r) => r.data as Blob);
  },
  // Audit
  getAuditLogs(params?: AdminAuditParams): Promise<AdminListResult<AuditLog>> {
    return api.get('/admin/audit', { params }).then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },
  // Stats
  getStats(): Promise<AdminStats> { return unwrap(api.get('/admin/stats')); },
};

export { isAxiosError };
export default api;
